import React, { useContext, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Container, Row, Form, Button, Table, InputGroup, Accordion } from 'react-bootstrap';
import { ToastContainer } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import axiosInstance from '../../utils/axios';
import { showToast } from '../reusables/CommonFunction';
import BusyLoaderOverlay from '../reusables/BusyLoaderOverlay';
import CustomPagination from '../reusables/CustomPagination';

const CreateSMSCampaign = () => {
    let { userDetails, clinicBranchDetails } = useContext(AuthContext)
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [campaignDetails, setCampaignDetails] = useState({ campaignName: "", campaignDate: null, message: "", sendType: "A", clinicBranchId: null });
    const [patientName, setPatientName] = useState("");
    const [patientDetails, setPatientDetails] = useState([]);
    const [selectedPatients, setSelectedPatients] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = parseInt(process.env.REACT_APP_PAGINATION_PAGE_SIZE);
    const salutation = { '1': 'DR.', '2': 'MR.', '3': 'MRS.', '4': 'MISS', '5': 'MASTER' }

    const getPatientDetails = (pageNumber) => {
        if (!patientName.trim()) {
            showToast("warning", "Please enter a patient name to search.");
            return;
        }

        setLoading(true);
        axiosInstance
            .get(`/patients/campaign_patient_details/`, {
                params: {
                    q: patientName,
                    clinicId: userDetails.clinic_id,
                    page: pageNumber
                }
            })
            .then(response => {
                if (response.status === 200) {
                    setPatientDetails(response.data.data)
                    setTotalCount(response.data.count);
                }
            })
            .catch(error => {
                showToast("error", "Failed to fetch patient details");
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const onPressSearchPatient = (oEvent) => {
        oEvent.preventDefault();
        setCurrentPage(1); // Reset to the first page
        getPatientDetails(1);
    }

    const onPressCreateCampaign = async (oEvent) => {
        oEvent.preventDefault();
        setIsSubmitting(true);

        if (!campaignDetails.campaignName.trim()) {
            showToast("warning", "Please enter a campaign name.");
            setIsSubmitting(false);
            return;
        }

        if (campaignDetails.sendType === "A" && !campaignDetails.campaignDate) {
            showToast("warning", "Please select a campaign date for the 'All Patients' campaign.");
            setIsSubmitting(false);
            return;
        }

        if (!campaignDetails.message.trim()) {
            showToast("warning", "Please enter a message for the campaign.");
            setIsSubmitting(false);
            return;
        }

        if (campaignDetails.sendType === "C" && selectedPatients.length === 0) {
            showToast("warning", "Please select at least one patient for the custom campaign.");
            setIsSubmitting(false);
            return;
        }

        setLoading(true);
        const payload = {
            clinic_id: userDetails.clinic_id,
            clinic_branch_id: campaignDetails.clinicBranchId || null,
            campaign_name: campaignDetails.campaignName,
            campaign_date: campaignDetails.campaignDate,
            campaign_message: campaignDetails.message,
            campaign_type: campaignDetails.sendType,
            campaign_status: "P",
            patients: selectedPatients.map(patient => ({
                patient_id: patient.patient_id,
            }))
        };

        try {
            const response = await axiosInstance.post('/sms-campaigns/', payload);
            if (response.status === 201) {
                setCampaignDetails({ campaignName: "", campaignDate: null, message: "", sendType: "A", clinicBranchId: null });
                setPatientDetails([]);
                setSelectedPatients([]);
                setPatientName("");
                showToast("success", "SMS Campaign created successfully.", {
                    onClose: () => {
                        navigate("/communication/sms-campaign/list", { replace: true });
                    }
                });
            } else if (response.status === 403 && response.data.detail === "Your clinic plan has expired.") {
                showToast("error", "You cannot create an SMS Campaign as your clinic plan has expired. Please renew your plan to manage campaigns.");
            }
        } catch (error) {
            if (error.response && error.response.status === 400) {
                showToast("error", "Failed to create SMS Campaign. Please check the input data.");
            }
            else if (error.response && error.response.status === 403 && error.response.data.detail === "Your clinic plan has expired.") {
                showToast("error", "You cannot create an SMS Campaign as your clinic plan has expired. Please renew your plan to manage campaigns.");
            }
            else {
                showToast("error", "An error occurred while creating the SMS Campaign.");
            }
        } finally {
            setLoading(false);
            setIsSubmitting(false);
        }
    }

    return (
        <>
            <Container className="ml-3 mr-3 mb-3">
                <Row>
                    <div className='col-md-12'>
                        <div className='content-section col-md-8'>
                            <legend className="border-bottom mb-4">Create SMS Campaign</legend>
                            <Form autoComplete="off" noValidate>
                                <Form.Group className="mb-3" controlId="formCampaignName">
                                    <Form.Label className="required">Campaign Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name='campaignName'
                                        value={campaignDetails.campaignName}
                                        placeholder="Enter campaign name"
                                        onChange={(e) => setCampaignDetails({ ...campaignDetails, campaignName: e.target.value })}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="formCampaignMessage">
                                    <Form.Label className="required">Message</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name='message'
                                        value={campaignDetails.message}
                                        onChange={(e) => setCampaignDetails({ ...campaignDetails, message: e.target.value })}
                                        maxLength={160}
                                        placeholder="Enter your SMS message here"
                                    />
                                    <Form.Text className="text-muted">
                                        {campaignDetails.message.length}/160 characters
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Send To</Form.Label>

                                    <div className="d-flex flex-wrap align-items-center gap-3">
                                        <div className="d-flex gap-2">
                                            <Button variant={campaignDetails.sendType === 'A' ? 'secondary' : 'outline-secondary'} onClick={(oEvent) => {
                                                oEvent.preventDefault();
                                                setPatientDetails([]);
                                                setSelectedPatients([]);
                                                setPatientName("");
                                                setCampaignDetails({ ...campaignDetails, sendType: 'A', campaignDate: null, clinicBranchId: null });
                                            }
                                            }>All Patients</Button>
                                            <Button variant={campaignDetails.sendType === 'C' ? 'secondary' : 'outline-secondary'} onClick={(oEvent) => {
                                                oEvent.preventDefault();
                                                setPatientDetails([]);
                                                setSelectedPatients([]);
                                                setPatientName("");
                                                setCampaignDetails({ ...campaignDetails, sendType: 'C', campaignDate: new Date().toISOString().split("T")[0], clinicBranchId: null });
                                            }}>Custom Patient List</Button>
                                        </div>
                                    </div>
                                </Form.Group>

                                {campaignDetails.sendType === "A" && (
                                    <div className="border rounded p-3 mb-3 bg-light">
                                        <Form.Text className="text-muted small d-block mb-3">
                                            <strong>Important:</strong> SMS will be sent to all registered patients tomorrow at <strong>8:00 AM</strong>. Delivery time may vary based on volume. Please ensure your message is suitable for all recipients.<br />
                                            <strong>Note:</strong> Only patients with valid mobile numbers will receive the SMS.
                                        </Form.Text>

                                        <Form.Group className="mb-3" controlId="formClinicBranch">
                                            <Form.Label>Clinic Branch</Form.Label>
                                            <Form.Select
                                                value={campaignDetails.clinicBranchId || ""}
                                                onChange={(oEvent) => setCampaignDetails({ ...campaignDetails, clinicBranchId: oEvent.target.value })}>
                                                <option value="">All Clinic Branch</option>
                                                {clinicBranchDetails.map((branch) => (
                                                    <option key={branch.clinic_branch_id} value={branch.clinic_branch_id}>
                                                        {branch.branch_address}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                )}

                                {campaignDetails.sendType === "C" && (
                                    <div className="border rounded p-3 mb-3 bg-light">
                                        <InputGroup className="mb-3" controlId="formPatientName">
                                            <Form.Control
                                                type="text"
                                                name="patientName"
                                                onChange={(oEvent) => setPatientName(oEvent.target.value)}
                                                placeholder="Enter Patient Name..."
                                                value={patientName}
                                            />
                                            <Button type="submit" variant="outline-secondary" onClick={(oEvent) => onPressSearchPatient(oEvent)}>
                                                Search
                                            </Button>
                                        </InputGroup>

                                        {patientDetails.length > 0 && (
                                            <div className='mb-3'>
                                                <Form.Text className="text-muted small d-block mb-3">
                                                    <strong>Important:</strong> SMS messages will be sent to the selected patients <strong>immediately</strong>. Delivery time may vary based on volume. Please ensure your message is suitable for all recipients.<br />
                                                    <strong>Note:</strong> Only patients with valid mobile numbers will receive the SMS.
                                                </Form.Text>
                                                <Table className='mb-3' bordered hover responsive>
                                                    <thead>
                                                        <tr>
                                                            <th>Patient Name</th>
                                                            <th>Mobile Number</th>
                                                            <th>Clinic Branch</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {patientDetails.map((patient) => {
                                                            const isSelected = selectedPatients.some(p => p.patient_id === patient.patient_id);
                                                            return (
                                                                <tr key={patient.patient_id}>
                                                                    <td>{salutation[patient.salutation]} {patient.patient_name}</td>
                                                                    <td>{patient.mobile_number}</td>
                                                                    <td>{patient.branch_name}</td>
                                                                    <td>
                                                                        <Button
                                                                            type="button"
                                                                            className="btn-sm"
                                                                            variant={isSelected ? "warning" : "primary"}
                                                                            onClick={(oEvent) => {
                                                                                oEvent.preventDefault();
                                                                                oEvent.stopPropagation();
                                                                                const alreadySelected = selectedPatients.some(p => p.patient_id === patient.patient_id);

                                                                                if (alreadySelected) {
                                                                                    setSelectedPatients(selectedPatients.filter(p => p.patient_id !== patient.patient_id));
                                                                                } else {
                                                                                    setSelectedPatients([...selectedPatients, patient]);
                                                                                }
                                                                            }}
                                                                        >{isSelected ? "Selected" : "Select"}</Button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </Table>

                                                <CustomPagination
                                                    totalCount={totalCount}
                                                    currentPage={currentPage}
                                                    pageSize={pageSize}
                                                    onPageChange={setCurrentPage} />
                                            </div>
                                        )}

                                        {selectedPatients.length > 0 && (
                                            <Accordion className="mt-3" defaultActiveKey="0">
                                                <Accordion.Item eventKey="0">
                                                    <Accordion.Header>Selected Patients ({selectedPatients.length})</Accordion.Header>
                                                    <Accordion.Body>
                                                        <Table bordered hover size="sm" responsive style={{ maxHeight: "300px", overflowY: "auto" }}>
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th>#</th>
                                                                    <th>Patient Name</th>
                                                                    <th>Mobile Number</th>
                                                                    <th>Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {selectedPatients.map((patient, idx) => (
                                                                    <tr key={idx}>
                                                                        <td>{idx + 1}</td>
                                                                        <td>{salutation[patient.salutation] || ""} {patient.patient_name}</td>
                                                                        <td>{patient.mobile_number}</td>
                                                                        <td>
                                                                            <Button
                                                                                variant="danger"
                                                                                size="sm"
                                                                                onClick={() => {
                                                                                    setSelectedPatients(selectedPatients.filter(p => p.patient_id !== patient.patient_id));
                                                                                }}
                                                                            >Remove</Button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        )}
                                    </div>
                                )}

                                <Form.Group className="mb-3" controlId="formCampaignDate">
                                    <Form.Label>Campaign Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="campaignDate"
                                        value={campaignDetails.sendType === "C" ? new Date().toISOString().split("T")[0] : campaignDetails.campaignDate || ""}
                                        min={campaignDetails.sendType === "C" ? new Date().toISOString().split("T")[0] : new Date(Date.now() + 86400000).toISOString().split("T")[0]} // minimum date is tomorrow
                                        max={campaignDetails.sendType === "C" ? new Date().toISOString().split("T")[0] : undefined} // maximum date is today for custom, otherwise no limit
                                        disabled={campaignDetails.sendType === "C"} // optional: make it read-only for custom
                                        onChange={(e) =>
                                            setCampaignDetails({
                                                ...campaignDetails,
                                                campaignDate: e.target.value,
                                            })
                                        }
                                    />
                                    <Form.Text className="text-muted">
                                        {campaignDetails.sendType === "C"
                                            ? "SMS will be sent immediately today. Date is auto-set and cannot be changed."
                                            : "Select a date from tomorrow onward. Messages will be sent at 8:00 AM on the selected date."}
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="formSubmit">
                                    <Button type="submit" variant="primary" disabled={userDetails.user_type === "M" || isSubmitting} onClick={onPressCreateCampaign} className="form-control">
                                        {isSubmitting ? "Submitting Campaign..." : "Submit Campaign"}
                                    </Button>
                                </Form.Group>
                            </Form>
                        </div>
                    </div>
                </Row>
            </Container>
            <ToastContainer />
            <BusyLoaderOverlay show={loading} />
        </>
    );
};

export default CreateSMSCampaign;
